import Vue from "vue";
import { Module } from "vuex";

export interface FeedEntry {
  id: string;
  title: string;
  subtitle: string;
  published: Date;
  apiUrl: string;
  thumbnailUrl: string;
  webUrl: string;
}

const PENDING = "pending";
const FETCH_ERROR = "fetch-error";
const BAD_STATUS = "bad-status";
const SUCCESS = "success";

// #region RequestState

interface Pending {
  status: typeof PENDING;
}

interface FetchError {
  status: typeof FETCH_ERROR;
  error: Error;
}

interface BadStatus {
  status: typeof BAD_STATUS;
  response: Response;
  data?: ResponseData;
}

interface Success {
  status: typeof SUCCESS;
  entries: FeedEntry[];
}

type RequestState = Pending | FetchError | BadStatus | Success;

// #endregion

export interface FeedPage {
  pageNumber: number;
  data: RequestState;
}

// #region Mutation Payloads

interface Mutation {
  pageNumber: number;
}

interface PendingMutation extends Mutation {
  type: typeof PENDING;
}

interface FetchErrorMutation extends Mutation {
  type: typeof FETCH_ERROR;
  error: TypeError | SyntaxError;
}

interface BadStatusMutation extends Mutation {
  type: typeof BAD_STATUS;
  response: Response;
  data?: ResponseData;
}

interface SuccessMutation extends Mutation {
  type: typeof SUCCESS;
  entries: FeedEntry[];
}

// #endregion

export interface LoadPageAction {
  pageSize: number;
  pageNumber: number;
}

interface ResponseData {
  response: {
    status: "ok" | string;
    total: number;
    startIndex: number;
    pageSize: number;
    currentPage: number;
    pages: number;
    orderBy: "newest" | "oldest" | "relevance";
    results: Array<{
      id: string;
      webPublicationDate: string;
      webTitle: string;
      webUrl: string;
      apiUrl: string;
      fields: {
        trailText: string;
        thumbnail: string;
      };
    }>;
  };
}

interface ModuleState {
  pages: FeedPage[];
}

function buildURL(pageSize: number, pageNumber: number): string {
  const includedFields = ["thumbnail", "trailText"];
  const excludedTags = [
    "theguardian/series/correctionsandclarifications",
    "crosswords/crosswords"
  ];

  const url = new URL(process.env.VUE_APP_SEARCH_URL);
  url.searchParams.set("api-key", process.env.VUE_APP_API_KEY);
  url.searchParams.set("page-size", pageSize.toString());
  url.searchParams.set("page", pageNumber.toString());
  url.searchParams.set("show-fields", includedFields.join(","));
  url.searchParams.set("tag", excludedTags.map(tag => `-${tag}`).join(","));
  return url.toString();
}

function sanitize(html: string): string {
  const template = document.createElement("template");
  template.innerHTML = html;
  return template.content.textContent || "";
}

const module: Module<ModuleState, {}> = {
  namespaced: true,
  state: { pages: [] },
  mutations: {
    [PENDING](state, { pageNumber }: PendingMutation) {
      const page: FeedPage = { pageNumber, data: { status: PENDING } };
      Vue.set(state.pages, pageNumber - 1, page);
    },
    [FETCH_ERROR](state, { pageNumber, error }: FetchErrorMutation) {
      const page: FeedPage = {
        pageNumber,
        data: { status: FETCH_ERROR, error }
      };
      Vue.set(state.pages, pageNumber - 1, page);
    },
    [BAD_STATUS](state, { pageNumber, response, data }: BadStatusMutation) {
      const page: FeedPage = {
        pageNumber,
        data: { status: BAD_STATUS, response, data }
      };
      Vue.set(state.pages, pageNumber - 1, page);
    },
    [SUCCESS](state, { pageNumber, entries }: SuccessMutation) {
      const page: FeedPage = { pageNumber, data: { status: SUCCESS, entries } };
      Vue.set(state.pages, pageNumber - 1, page);
    }
  },
  actions: {
    async loadPage({ commit }, { pageSize, pageNumber }: LoadPageAction) {
      // TODO: use date-based cursor to prevent page entry drift

      commit<PendingMutation>({ type: PENDING, pageNumber });

      try {
        const response = await fetch(buildURL(pageSize, pageNumber));

        if (!response.ok) {
          commit<BadStatusMutation>({ type: BAD_STATUS, pageNumber, response });
          return;
        }

        const data: ResponseData = await response.json();

        if (data.response.status !== "ok") {
          commit<BadStatusMutation>({
            type: BAD_STATUS,
            pageNumber,
            response,
            data
          });
          return;
        }

        const entries: FeedEntry[] = data.response.results.map(result => ({
          id: result.id,
          title: result.webTitle,
          subtitle: sanitize(result.fields.trailText),
          published: new Date(result.webPublicationDate),
          apiUrl: result.apiUrl,
          thumbnailUrl: result.fields.thumbnail,
          webUrl: result.webUrl
        }));

        commit<SuccessMutation>({ type: SUCCESS, pageNumber, entries });
      } catch (error) {
        commit<FetchErrorMutation>({ type: FETCH_ERROR, pageNumber, error });
      }
    }
  }
};

export default module;
